import { useState, useCallback, useEffect, useRef } from 'react'
import type { OnboardingStatus, OnboardingCompleteTourResponse } from '@/types'

const STORAGE_KEY = 'onboarding_tour_dismissed'

interface UseOnboardingReturn {
  status: OnboardingStatus | null
  isLoading: boolean
  isTourRunning: boolean
  isTourDismissed: boolean
  startTour: () => void
  completeTour: () => Promise<OnboardingCompleteTourResponse | null>
  dismissTour: () => void
  restartTour: () => void
  refreshStatus: () => Promise<void>
}

interface UseOnboardingOptions {
  // If true, delays auto-start to allow badge modals to be shown first
  hasBadgesToShow?: boolean
}

export function useOnboarding(
  initialStatus: OnboardingStatus | null,
  options: UseOnboardingOptions = {}
): UseOnboardingReturn {
  const { hasBadgesToShow = false } = options

  const [status, setStatus] = useState<OnboardingStatus | null>(initialStatus)
  const [isLoading, setIsLoading] = useState(false)
  const [isTourRunning, setIsTourRunning] = useState(false)
  const [isTourDismissed, setIsTourDismissed] = useState(false)

  // Ref to prevent multiple completeTour calls
  const isCompletingRef = useRef(false)
  // Ref to track if tour has been auto-started
  const hasAutoStartedRef = useRef(false)
  // Ref to track if tour has been completed (prevents auto-restart)
  const hasCompletedRef = useRef(false)

  // Check localStorage for dismissed state on mount
  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY)
    if (dismissed === 'true') {
      setIsTourDismissed(true)
    }
    // Sync hasCompletedRef with initial status from server
    if (initialStatus?.tour_completed) {
      hasCompletedRef.current = true
    }
  }, [initialStatus?.tour_completed])

  // Auto-start tour for new users who haven't completed or dismissed it
  useEffect(() => {
    // CRITICAL: Check completion status FIRST before any other checks
    // This prevents auto-restart after tour completion
    if (hasCompletedRef.current) {
      return
    }

    // Only auto-start once and if conditions are met
    if (
      status &&
      !status.tour_completed &&
      !status.is_completed &&
      !isTourDismissed &&
      !hasAutoStartedRef.current &&
      !isTourRunning
    ) {
      // If badges are showing, wait for them to be closed
      if (hasBadgesToShow) {
        const handleBadgeModalsComplete = () => {
          if (!hasAutoStartedRef.current) {
            hasAutoStartedRef.current = true
            // Small delay after badge modal closes
            setTimeout(() => setIsTourRunning(true), 300)
          }
        }
        window.addEventListener('badgeModalsComplete', handleBadgeModalsComplete)
        return () => window.removeEventListener('badgeModalsComplete', handleBadgeModalsComplete)
      } else {
        // No badges showing, start after short delay
        hasAutoStartedRef.current = true
        const timeout = setTimeout(() => {
          setIsTourRunning(true)
        }, 500)
        return () => clearTimeout(timeout)
      }
    }
  }, [status, isTourDismissed, isTourRunning, hasBadgesToShow])

  const refreshStatus = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/onboarding/status')
      if (response.ok) {
        const data = await response.json()
        setStatus(data)
      }
    } catch (error) {
      console.error('Error fetching onboarding status:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const startTour = useCallback(() => {
    setIsTourRunning(true)
    setIsTourDismissed(false)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const completeTour = useCallback(async (): Promise<OnboardingCompleteTourResponse | null> => {
    // Prevent multiple simultaneous calls
    if (isCompletingRef.current || hasCompletedRef.current) {
      return null
    }

    isCompletingRef.current = true
    setIsTourRunning(false)
    setIsLoading(true)

    try {
      const response = await fetch('/onboarding/complete_tour', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
      })

      if (!response.ok) {
        return null
      }

      const data: OnboardingCompleteTourResponse = await response.json()

      if (data.success && data.status) {
        setStatus(data.status)
        hasCompletedRef.current = true
      }

      return data
    } catch (error) {
      console.error('Error completing tour:', error)
      return null
    } finally {
      setIsLoading(false)
      isCompletingRef.current = false
    }
  }, [])

  const dismissTour = useCallback(() => {
    setIsTourRunning(false)
    setIsTourDismissed(true)
    localStorage.setItem(STORAGE_KEY, 'true')
  }, [])

  const restartTour = useCallback(() => {
    setIsTourDismissed(false)
    localStorage.removeItem(STORAGE_KEY)
    setIsTourRunning(true)
  }, [])

  return {
    status,
    isLoading,
    isTourRunning,
    isTourDismissed,
    startTour,
    completeTour,
    dismissTour,
    restartTour,
    refreshStatus,
  }
}
